// exposure.js, deterministic astrophotography exposure recipes.
//
// Pure + client-side: My Kit + a target class (+ optional sky conditions) ->
// a concrete recipe (ISO / aperture / shutter / subs / total integration) with
// a one-line rationale and depth notes. No network, no AI. The AI layer only
// adds prose flavour on top of these numbers, so server cost stays flat as the
// user base grows.
//
// Honesty: the untracked star-trailing limit is the 500-rule on the crop-
// adjusted focal length. We don't store pixel pitch, so we don't fake the
// stricter NPF rule, the 500-rule is the stated baseline. Every field degrades
// gracefully when kit values are null (numerics may arrive as strings from pg,
// so everything is coerced through num()).

const CROP = { full_frame: 1.0, aps_c: 1.5, m43: 2.0, one_inch: 2.7, phone: 5.6, unknown: 1.0 };
const ISO_STOPS = [100, 200, 400, 800, 1600, 3200, 6400, 12800];

const num = v => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

export function cropFactor(sensorClass) {
  return CROP[sensorClass] ?? 1.0;
}

export function formatShutter(s) {
  if (s == null) return '-';
  if (s >= 1) return Math.round(s) + 's';
  return '1/' + Math.round(1 / s) + 's';
}

export function fLabel(f) {
  if (f == null) return '-';
  return 'f/' + (f >= 8 ? String(Math.round(f)) : (Math.round(f * 10) / 10));
}

function snapIso(iso) {
  return ISO_STOPS.reduce((b, v) => (Math.abs(v - iso) < Math.abs(b - iso) ? v : b), ISO_STOPS[0]);
}

// Map the app's many type strings down to one recipe class.
function normalizeKind(t) {
  const s = String(t || '').toLowerCase();
  if (/planet/.test(s)) return 'planet';
  if (/moon|lunar/.test(s)) return 'moon';
  if (/nebula/.test(s)) return 'nebula';
  if (/galaxy/.test(s)) return 'galaxy';
  if (/cluster/.test(s)) return 'cluster';
  if (/milky/.test(s)) return 'milkyway';
  if (/meteor|shower/.test(s)) return 'meteor';
  if (/sat|pass|iss/.test(s)) return 'satellite';
  return 'widefield';
}

// Brighter sky -> pull ISO down (less skyglow amplification); darker -> allow more.
function isoForSky(baseIso, bortle) {
  if (bortle == null) return baseIso;
  if (bortle >= 7) return baseIso / 2;
  if (bortle >= 5) return baseIso / 1.4;
  if (bortle <= 2) return baseIso * 1.4;
  return baseIso;
}

const KIND_LABEL = {
  planet: 'planet', moon: 'the Moon', nebula: 'nebula', galaxy: 'galaxy',
  cluster: 'star cluster', milkyway: 'Milky Way', meteor: 'meteor shower',
  satellite: 'satellite pass', widefield: 'wide field',
};

const DSO = ['nebula', 'galaxy'];

export function computeExposure({ kit, target, conditions } = {}) {
  // Null-safe: useKit yields null (not undefined) before a kit exists, and a
  // destructuring default only covers undefined, a null kit crashed the whole
  // tree when a fixed-target row expanded (the "Venus black screen").
  kit = kit || {}; target = target || {}; conditions = conditions || {};
  if (kit.needs_review || kit.setup_type === 'legacy') return {
    ...visualRecipe(normalizeKind(target.kind || target.type)),
    why:'Review the combined equipment entry in Edit Kit before using exposure settings.',
    notes:['Choose one connected imaging setup and save independent equipment in separate profiles.']
  };
  if (kit.setup_type === 'smart_telescope') return {
    optic:'smart',iso:null,iso_label:'App controlled',aperture_label:fLabel(num(kit.aperture_f)),
    shutter_label:'App controlled',subs_label:'Live stack',total_minutes:null,tracking:'integrated',rule:'smart telescope',
    target_label:KIND_LABEL[normalizeKind(target.kind || target.type)],
    why:'Use the telescope app to select a supported target, autofocus and accumulate a live stack.',
    notes:['The built-in camera and mount form one imaging setup. Exposure and gain depend on the telescope app.',
      /sat|meteor|planet/.test(String(target.kind || target.type)) ? 'Check manufacturer target support; this setup may not support this activity.' : 'Check target altitude, framing and available dark time in the telescope app.']
  };
  const sensor   = kit.sensor_class || 'unknown';
  const focal    = num(kit.focal_length_mm);
  const maxAp    = num(kit.aperture_f);                 // widest aperture (smallest f-number)
  const tracker  = kit.mount_type ? kit.mount_type === 'equatorial' : !!kit.has_tracker;
  const hasScope = !!(kit.scope && String(kit.scope).trim());
  const kind     = normalizeKind(target.kind || target.type);
  const bortle   = num(conditions.bortle);
  const moonBright = !!conditions.moon_up && (num(conditions.moon_phase_fraction) ?? 0) > 0.4;

  // No usable camera -> phone or visual guidance.
  if (sensor === 'phone') return phoneRecipe(kind);
  if (focal == null && !hasScope) return visualRecipe(kind);

  const crop      = cropFactor(sensor);
  const effFocal  = focal != null ? Math.round(focal * crop) : null;
  const untracked = effFocal ? Math.max(1, Math.round(500 / effFocal)) : 20;  // 500-rule, seconds
  const wideOpen  = maxAp || 2.8;
  const wideDSO   = Math.max(wideOpen, 2.8);            // don't open past f/2.8 (coma)

  const notes = [];
  if (kit.mount_type === 'altaz') notes.push('Alt-az tracking retains field rotation. Use short exposures and verify your stacking workflow.');
  let r;

  switch (kind) {
    case 'moon':
      r = { iso: 200, aperture: Math.max(8, wideOpen), shutter: 1 / 250, subs: 1,
            tracking: 'not_needed', rule: 'lunar (bright)',
            why: 'The Moon is sunlit rock, low ISO, stopped down, fast shutter.' };
      notes.push('Meter on the lit surface; the surrounding sky should fall to black.');
      if (effFocal && effFocal < 300) notes.push('It stays small under ' + effFocal + 'mm, crop in, or reach for a longer lens or scope.');
      break;

    case 'planet':
      r = { iso: 400, aperture: Math.max(5.6, wideOpen), shutter: 1 / 100, subs: 1,
            tracking: 'recommended', rule: 'planetary',
            why: 'Planets are tiny and bright, freeze the seeing, then stack many frames.' };
      notes.push('A video + lucky-imaging stack (AutoStakkert/RegiStax) beats any single frame.');
      if (!hasScope) notes.push('Long focal length matters most here, a scope helps far more than a lens.');
      break;

    case 'nebula':
      if (tracker) {
        r = { iso: isoForSky(800, bortle), aperture: wideDSO, shutter: 120, subs: 24,
              tracking: 'required', rule: 'tracked sub',
              why: 'Tracked long subs gather faint Hα; stacking drives the noise down.' };
        notes.push('Bright cores (e.g. M42) clip, shoot a few 15s subs too, to recover them in processing.');
      } else {
        r = { iso: isoForSky(3200, bortle), aperture: wideDSO, shutter: untracked, subs: 60,
              tracking: 'recommended', rule: '500-rule + deep stack',
              why: 'Untracked: many short high-ISO subs, stacked, stand in for one long exposure.' };
        notes.push('A star tracker is the single biggest upgrade for nebulae, it unlocks 1–2 min subs.');
      }
      break;

    case 'galaxy':
      if (tracker) {
        r = { iso: isoForSky(1600, bortle), aperture: wideDSO, shutter: 180, subs: 30,
              tracking: 'required', rule: 'tracked sub',
              why: 'Galaxies are faint and small, long subs and a lot of total integration.' };
        notes.push('Integration time is everything here; 2+ hours is what turns a smudge into spiral arms.');
      } else {
        r = { iso: isoForSky(3200, bortle), aperture: wideDSO, shutter: untracked, subs: 80,
              tracking: 'recommended', rule: '500-rule + deep stack',
              why: 'Untracked galaxy work is hard, short subs, high ISO, a very deep stack.' };
        notes.push('Realistically a tracker is needed for galaxies; without one, favour brighter targets.');
      }
      break;

    case 'cluster':
      r = { iso: isoForSky(800, bortle), aperture: Math.max(wideDSO, wideOpen),
            shutter: tracker ? 45 : untracked, subs: tracker ? 20 : 15,
            tracking: tracker ? 'recommended' : 'not_needed', rule: tracker ? 'tracked sub' : '500-rule',
            why: 'Clusters are points of light, careful focus and moderate subs over brute-force time.' };
      notes.push('Pinpoint stars reward exact focus more than longer exposure.');
      break;

    case 'meteor':
      r = { iso: isoForSky(3200, bortle), aperture: wideOpen, shutter: Math.min(untracked, 25), subs: 999,
            tracking: 'not_needed', rule: 'wide nightscape',
            why: 'Wide and fast, fired continuously, most frames are empty, then one catches a streak.' };
      notes.push('Use an intervalometer: back-to-back 15–25s frames all night, aimed ~40° off the radiant.');
      break;

    case 'satellite':
      r = { iso: isoForSky(1600, bortle), aperture: wideOpen, shutter: 15, subs: 1,
            tracking: 'not_needed', rule: 'streak',
            why: 'Let the pass draw its own line, one open exposure records the trail.' };
      notes.push('Frame where it peaks and start the exposure a beat before it enters frame.');
      break;

    default: // milkyway / widefield
      r = { iso: isoForSky(3200, bortle), aperture: wideOpen, shutter: tracker ? 60 : untracked, subs: tracker ? 6 : 1,
            tracking: 'not_needed', rule: tracker ? 'tracked' : '500-rule',
            why: tracker
              ? 'A tracker lets you drop ISO and lengthen the sky exposure for a cleaner result.'
              : 'Widest aperture, ISO high, shutter at the 500-rule limit before stars trail.' };
      if (!tracker && effFocal) notes.push('At ' + effFocal + 'mm-equiv the 500-rule caps you near ' + untracked + 's before stars streak.');
      break;
  }

  // Sky / moon refinements.
  if (moonBright && (DSO.includes(kind) || kind === 'milkyway' || kind === 'meteor')) {
    notes.push('Moon is up and bright, skyglow will wash out faint detail; wait for it to set, or use a filter.');
  }
  if (bortle != null && bortle >= 6 && DSO.includes(kind)) {
    notes.push('Bortle ' + bortle + ': a dual-narrowband filter cuts the city glow and is the highest-impact buy.');
  }
  if (hasScope && ['planet', 'moon', 'galaxy', 'nebula'].includes(kind)) {
    notes.unshift('Shoot through your ' + String(kit.scope).trim() + ', its focal length, not the lens, sets the framing.');
  }
  notes.push('Focus on a bright star in live-view at max magnification before you start; refocus as the air cools.');

  if (hasScope) r.aperture = maxAp;
  r.iso           = snapIso(r.iso);
  r.subs_label    = r.subs >= 999 ? 'continuous' : ('×' + r.subs);
  r.total_minutes = (r.subs >= 999 || r.subs <= 1) ? null : Math.round((r.shutter * r.subs) / 60);
  r.shutter_label = formatShutter(r.shutter);
  r.aperture_label = fLabel(r.aperture);
  r.optic         = hasScope && ['planet', 'moon', 'galaxy', 'nebula'].includes(kind) ? 'scope' : 'camera';
  r.target_label  = KIND_LABEL[kind];
  r.notes         = notes;
  return r;
}

function phoneRecipe(kind) {
  const easy = ['moon', 'planet', 'satellite', 'cluster', 'widefield', 'milkyway'].includes(kind);
  return {
    optic: 'phone', iso: null, aperture: null, shutter: null, subs: null, total_minutes: null,
    tracking: 'not_needed', rule: 'phone night mode',
    iso_label: 'auto', aperture_label: 'fixed', shutter_label: 'Night mode',
    target_label: KIND_LABEL[kind] || 'the sky',
    why: easy
      ? 'Phones do great on the Moon, bright planets, the ISS and constellations, let Night Mode work.'
      : 'Deep-sky is beyond a phone alone, but Night Mode still captures more than your eyes can see.',
    notes: [
      'Brace the phone against something solid or use a small tripod, any shake smears the stars.',
      'Turn on the longest Night Mode exposure (10–30s) and tap to lock focus on a bright star.',
      (kind === 'nebula' || kind === 'galaxy')
        ? 'For real deep-sky, a clip-on tracker plus a camera is the next step.'
        : 'Skip digital zoom, crop afterwards instead.',
    ],
  };
}

function visualRecipe(kind) {
  return {
    optic: 'eyes', iso: null, aperture: null, shutter: null, subs: null, total_minutes: null,
    tracking: 'n/a', rule: 'visual', shutter_label: '-', aperture_label: '-',
    target_label: KIND_LABEL[kind] || 'the sky',
    why: 'No camera in your kit yet. Here is how to get the most from your eyes and binoculars.',
    notes: [
      'Give your eyes 20 minutes in the dark and avoid white light to stay adapted.',
      ['nebula', 'galaxy', 'cluster', 'milkyway'].includes(kind)
        ? 'Use averted vision, look slightly to the side of a faint target and it brightens.'
        : 'Binoculars (7×50 or 10×50) transform the Moon, Jupiter’s moons and bright clusters.',
      'Add a camera to My Kit and exact exposure recipes will appear here.',
    ],
  };
}
